import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Wifi, WifiOff } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { API } from '../App';

// Try to import WebSocket context - will fail gracefully if not available
let useWebSocketContext = null;
try {
  const wsContext = require('../context/WebSocketContext');
  useWebSocketContext = wsContext.useWebSocketContext;
} catch (e) {
  console.log('WebSocket context not available');
}

const NotificationsDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Try to use WebSocket context for real-time updates
  let wsContext = null;
  try {
    if (useWebSocketContext) {
      wsContext = useWebSocketContext();
    }
  } catch (e) {
    // Not inside WebSocket provider
  }

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.slice(0, 10)); // Show last 10
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds (fallback when WebSocket not available)
    const interval = setInterval(() => {
      fetchNotifications();
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  // Listen for real-time notifications from WebSocket
  useEffect(() => {
    if (wsContext?.notifications?.length > 0) {
      // Refresh notifications when we receive real-time updates
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [wsContext?.notifications, fetchNotifications, fetchUnreadCount]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment': return '📅';
      case 'message': return '💬';
      case 'payment': return '💳';
      case 'membership': return '🎫';
      default: return '🔔';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500"
              data-testid="unread-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {/* WebSocket connection indicator */}
          {wsContext && (
            <span className="absolute -bottom-1 -right-1">
              {wsContext.isConnected ? (
                <Wifi className="h-3 w-3 text-green-500" />
              ) : (
                <WifiOff className="h-3 w-3 text-gray-400" />
              )}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {wsContext?.isConnected && (
            <span className="flex items-center text-xs text-green-600">
              <Wifi className="h-3 w-3 mr-1" />
              Live
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No notifications
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {notifications.map((notif) => (
              <DropdownMenuItem
                key={notif.id}
                className={`flex flex-col items-start p-3 cursor-pointer ${!notif.is_read ? 'bg-blue-50' : ''}`}
                onClick={() => !notif.is_read && markAsRead(notif.id)}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex justify-between w-full items-start">
                  <span className="font-medium text-sm flex items-center gap-1">
                    <span>{getNotificationIcon(notif.type)}</span>
                    {notif.title}
                  </span>
                  {!notif.is_read && (
                    <Badge variant="default" className="text-xs ml-2 bg-teal-500">New</Badge>
                  )}
                </div>
                <span className="text-xs text-gray-600 mt-1">{notif.message}</span>
                <span className="text-xs text-gray-400 mt-1">
                  {new Date(notif.created_at).toLocaleString()}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
